/*
 * Purpose: Execution results — human overlay on JSON with eye toggle per block card
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { useState } from 'react';
import { ExecutionContext } from '@/services/executor';
import type { Node, Workflow } from '@/stores/workflowStore';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle, Clock, Eye, Braces } from 'lucide-react';
import { getNodeLabel } from '@/lib/workflowUtils';
import { getNodePurposeLine } from '@/lib/mapNodeSummary';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import {
  formatExecutionResultJson,
  summarizeExecutionResult,
} from '@/lib/executionResultSummary';
import { cn } from '@/lib/utils';

interface ExecutionResultsProps {
  results: ExecutionContext[];
  workflow?: Workflow | null;
}

function ResultOutputPanel({
  output,
  node,
  errors,
}: {
  output: unknown;
  node?: Node;
  errors: string[];
}) {
  const [showRaw, setShowRaw] = useState(false);
  const summary = summarizeExecutionResult(output, node, errors);
  const json = formatExecutionResultJson(output);
  const hasPayload = output != null || errors.length === 0;

  if (!hasPayload && errors.length === 0) return null;

  return (
    <div className="relative mt-2 rounded-md border border-border/60 overflow-hidden min-h-[4.5rem]">
      {/* Raw JSON layer — always present underneath */}
      <pre
        className={cn(
          'text-[10px] sm:text-xs bg-muted/40 p-2.5 overflow-auto max-h-36 whitespace-pre-wrap break-words font-mono transition-opacity',
          !showRaw && 'opacity-40 blur-[0.4px] select-none'
        )}
        aria-hidden={!showRaw}
      >
        {errors.length > 0 ? errors.join('\n') : json}
      </pre>

      {/* Human-readable overlay — default on */}
      {!showRaw && (
        <div
          className={cn(
            'absolute inset-0 flex flex-col justify-between p-2.5 sm:p-3',
            'bg-card/92 backdrop-blur-[2px] border-0'
          )}
        >
          <div className="min-w-0 flex-1 pr-8">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              {summary.status === 'simulated' && (
                <Badge variant="secondary" className="text-[9px] h-4 px-1.5">
                  Simulated
                </Badge>
              )}
              {summary.status === 'error' && (
                <Badge variant="destructive" className="text-[9px] h-4 px-1.5">
                  Failed
                </Badge>
              )}
            </div>
            <p className="text-sm font-medium leading-snug text-foreground line-clamp-3">
              {summary.headline}
            </p>
            {summary.detail && (
              <p className="text-xs text-muted-foreground mt-1.5 line-clamp-4 leading-relaxed">
                {summary.detail}
              </p>
            )}
          </div>
        </div>
      )}

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'absolute top-1.5 right-1.5 h-7 w-7 shrink-0',
          'bg-background/80 hover:bg-background shadow-sm border border-border/50'
        )}
        onClick={() => setShowRaw((v) => !v)}
        aria-label={showRaw ? 'Show human-readable summary' : 'Show raw JSON'}
        title={showRaw ? 'Show summary' : 'Show raw JSON'}
      >
        {showRaw ? (
          <Eye className="h-3.5 w-3.5 text-foreground" />
        ) : (
          <Braces className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </Button>
    </div>
  );
}

export function ExecutionResults({ results, workflow }: ExecutionResultsProps) {
  if (results.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No execution results yet. Run a workflow to see results.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {results.map((result) => {
        const node = workflow?.nodes.find((n) => n.id === result.nodeId);
        const label = node ? getNodeLabel(node) : result.nodeId;
        const typeLabel = node ? getBlockTypeLabel(node.type, node.subtype) : 'Block';
        const purpose = node ? getNodePurposeLine(node) : null;
        const hasError = result.errors.length > 0;

        return (
          <Card key={result.nodeId} className="p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">{label}</p>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
                  {typeLabel}
                </p>
                {purpose && purpose !== label && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{purpose}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {hasError ? (
                  <AlertCircle className="w-4 h-4 text-destructive" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                )}
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {new Date(result.timestamp).toLocaleTimeString()}
                </span>
              </div>
            </div>

            <ResultOutputPanel output={result.output} node={node} errors={result.errors} />
          </Card>
        );
      })}
    </div>
  );
}
